import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { DealScheduleSummary } from "@/components/schedule/DealScheduleSummary";
import type { BuyXGetYDeal } from "@/lib/api/types";
import { formatCents } from "@/lib/utils/number";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  deal: BuyXGetYDeal | null;
};

/** Read-only mirror of MultiBuyDealFormDialog's fields — no editing, no customer preview. */
export function MultiBuyDealDetailDialog({ open, onOpenChange, deal }: Props) {
  // Keeps content rendered through the close animation instead of unmounting mid-transition.
  const [prevDeal, setPrevDeal] = useState(deal);
  const [lastDeal, setLastDeal] = useState(deal);
  if (deal !== prevDeal) {
    setPrevDeal(deal);
    if (deal) setLastDeal(deal);
  }
  const display = deal ?? lastDeal;

  const discountText = display
    ? display.discount_type === "percent"
      ? display.discount_value >= 100
        ? "Free"
        : `${display.discount_value}% off`
      : `${formatCents(display.discount_value)} each`
    : "";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        {display && (
          <>
            <DialogHeader>
              <DialogTitle>{display.name}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <p className="text-xs font-medium text-muted-foreground mb-1.5">Item</p>
                <p className="text-sm font-medium">{display.menu_item.name}</p>
              </div>
              <div>
                <p className="text-xs font-medium text-muted-foreground mb-1.5">Buy / Get</p>
                <p className="text-sm font-medium">
                  Buy {display.buy_quantity}, get {display.get_quantity} at {discountText}
                </p>
              </div>
              <div>
                <p className="text-xs font-medium text-muted-foreground mb-1.5">Rule</p>
                <p className="text-sm font-medium">{display.rule_label}</p>
              </div>
              {display.max_claims_per_customer_per_day != null && (
                <div>
                  <p className="text-xs font-medium text-muted-foreground mb-1.5">Claim limit</p>
                  <p className="text-sm font-medium">
                    {display.max_claims_per_customer_per_day}/customer/day
                  </p>
                </div>
              )}
              <div>
                <p className="text-xs font-medium text-muted-foreground mb-1.5">Schedule</p>
                <DealScheduleSummary schedule={display.schedule} />
              </div>
              <div>
                <p className="text-xs font-medium text-muted-foreground mb-1.5">Status</p>
                <p className="text-sm font-medium capitalize">{display.status}</p>
              </div>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
